<template>
    <div>
        <div v-if="activate">
            <div v-if="page === 1" class="p-grid overlayContent">
                <div class="p-col-fixed p-mx-auto p-mb-3 overlayContent--qr">
                    <div class="image overlayContent--qrImg">
                        <p v-if="isLoading">loading...</p>
                        <qr-code
                            :text="accessLink"
                            :size="220"
                            error-level="M"
                            v-else
                        ></qr-code>
                    </div>
                </div>
                <div class="p-col overlayContent--apps">
                    <p class="overlayContent--appsDesc">
                        {{ $t('description.setup') }}
                    </p>
                    <p class="overlayContent--appsAndroid b-device">
                        <Button
                            data-id="goToAndroidStoreButton"
                            type="button"
                            icon="pi pi-android"
                            :label="$t('description.link.android')"
                            @click="goToLink(url.android)"
                        ></Button>
                    </p>
                    <p class="overlayContent--appsApple b-device">
                        <Button
                            data-id="goToIOSStoreButton"
                            type="button"
                            icon="pi pi-apple"
                            :label="$t('description.link.ios')"
                            @click="goToLink(url.ios)"
                        ></Button>
                    </p>
                </div>
                <p class="overlayContent--qrDesc">
                    {{ $t('description.code') }}
                </p>
                <p class="highlight overlayContent--qrCodes" v-if="isLoading">
                    loading...
                </p>
                <p
                    v-else
                    class="highlight p-flex-wrap overlayContent--qrCodes"
                    v-html="addSpaces(accessCode, 4)"
                ></p>
            </div>
            <div v-if="page === 2">
                <p>{{ $t('description.input.on') }}</p>
                <div class="p-field">
                    <input
                        v-model="confirmCode"
                        oninput="this.value = this.value.replace(/[^0-9.]/g, '').replace(/(\.*)\./g, '');"
                        aria-describedby="confirmcode-help"
                        class="p-inputtext p-component"
                        :class="confirmCodeInvalid ? 'p-invalid' : ''"
                        data-id="confirmcode-input"
                    />
                    <small
                        id="confirmcode-help"
                        :class="confirmCodeInvalid ? 'p-invalid' : ''"
                        data-id="confirmcode-help"
                        >{{
                            confirmCodeInvalid
                                ? $t('description.confirmcode.invalid')
                                : $t('description.confirmcode.help')
                        }}</small
                    >
                </div>
            </div>
        </div>
        <div v-if="page >= 3">
            <p>{{ $t('description.backup') }}</p>
            <div class="p-grid backupcodes">
                <div
                    class="p-col-6 backup-code"
                    v-for="backupCode in backupCodes"
                    :key="backupCode"
                    v-html="backupCode"
                ></div>
            </div>
        </div>
        <footer>
            <Button
                data-id="tfaButton-cancel"
                v-if="page <= 2"
                :label="$t('common.buttons.cancel')"
                @click="cancelLogin"
                class="p-button-secondary"
            />
            <Button
                v-if="page === 1 && activate"
                :label="$t('common.buttons.next')"
                @click="goForward"
                class="p-button"
                data-id="tfaButton-next"
            />
            <Button
                v-if="page === 2"
                :label="$t('common.buttons.ok')"
                :disabled="disableSubmitButton()"
                @click="sendTfaCode"
                class="p-button"
                data-id="tfaButton-ok"
            />
            <Button
                data-id="tfaButton-done"
                v-if="page > 2"
                :label="$t('common.buttons.done')"
                @click="cancelLogin"
                class="p-button"
            />
        </footer>
    </div>
</template>

<script>
    import Button from 'primevue/button';
    import VueQRCodeComponent from 'vue-qrcode-component';
    import store from '@/store/store';

    export default {
        name       : 'TfaActivation',
        components : {
            Button,
            QrCode : VueQRCodeComponent,
        },
        props : {
            activate : { type : Boolean, default : true },
        },
        methods : {
            // NOTE: all information in this component should be used only by the
            // component. Thus, no Vuex storage will be used except to fetch data
            // already stored there.
            getAccessLink() {
                if (!this.getLoggedUser.tfa) {
                    this.$apiClient
                        .get('tfaSecret', { userId : this.getLoggedUser.id })
                        .then((response) => {
                            this.accessLink = response.data.data.url;
                            this.accessCode = response.data.data.secret;
                            this.isLoading = false;
                            this.$emit('refresh-user');
                        });
                }
            },
            goForward() {
                this.page++;
            },
            checkAccess() {
                if (!store.getters.isAwaitingActivation) {
                    this.$router.push({ name : 'login' });
                }
            },
            goToLink(url) {
                window.open(url);
            },
            disableSubmitButton() {
                let buttonDisabled = ('' + this.confirmCode).length < 6;
                if (buttonDisabled) {
                    this.confirmCodeInvalid = false;
                }
                return buttonDisabled;
            },
            sendTfaCode() {
                this.$apiClient
                    .post(
                        'tfa',
                        { userId : this.getLoggedUser.id },
                        {
                            code   : this.confirmCode,
                            status : true,
                        }
                    )
                    .then(() => {
                        this.$apiClient
                            .get('tfaBackup', { userId : this.getLoggedUser.id })
                            .then((response) => {
                                this.backupCodes = response.data.data;
                                this.page++;
                            });
                    })
                    .then(() => {
                        this.$emit('enable-tfa');
                    })
                    .catch((error) => {
                        if (
                            error.parsedErrors.messages[0] ===
                            'MAX_ALLOWED_CODE_ATTEMPTS'
                        ) {
                            this.$store
                                .dispatch('logout')
                                .then(() => this.$router.push('login'));
                        }
                        this.confirmCodeInvalid = true;
                    });
            },
            addSpaces(value, size = 4) {
                let returnString = '<span>';
                for (let i = 0; i < value.length; i++) {
                    if (Number.isInteger(i / size) && i > size - 1) {
                        returnString += '</span><span>';
                    }
                    returnString += value.substring(i, i + 1);
                }
                returnString += '</span>';
                return returnString;
            },
            cancelLogin() {
                this.$store
                    .dispatch('logout')
                    .then(() => this.$router.push('login'));
            },
        },
        beforeMount() {
            this.checkAccess();
            this.getAccessLink();
        },
        computed : {
            getLoggedUser() {
                return this.$store.getters.getLoggedUser;
            },
        },
        data() {
            return {
                page               : 1,
                accessLink         : '',
                accessCode         : '',
                waiting            : false,
                confirmCode        : null,
                confirmCodeInvalid : false,
                backupCodes        : [],
                isLoading          : true,
                reAuthenticated    : false,
                reAuthPassword     : '',
                isReAuthenticating : false,
                failedReAuth       : false,
                url                : {
                    android :
                        'https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2',
                    ios : 'https://apps.apple.com/app/google-authenticator/id388497605',
                },
            };
        },
    };
</script>
<style scoped lang="scss">
    .reauthPassword {
        max-width: 320px;
    }

    .validationMsg {
        display: inline-block;
    }

    .overlayContent--qr {
        width: 240px;
        height: 220px;
    }

    .overlayContent--apps {
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        justify-content: space-around;

        .overlayContent--appsDesc {
            width: 100%;
            margin-top: 0;
        }
    }

    .overlayContent--qrDesc {
        padding: 0 10px;
    }

    .overlayContent--qrCodes {
        width: 100%;
        display: flex;
        justify-content: center;
    }

    #confirmcode-help {
        display: block;
    }

    .p-invalid {
        color: #f44336;
    }

    .p-inputtext.p-invalid.p-component {
        border-color: #f44336;
        color: #000000;
    }

    .b-device {
        margin-top: 0;
        margin-bottom: 0;
        width: 240px;
    }

    .backupcodes {
        padding-bottom: 20px;
    }

    body .highlight {
        font-size: 12px !important;
    }

    body .p-button {
        margin-top: 20px;
    }
</style>

<i18n src="myra-ui-common/i18n/common.json" />
<i18n src="./i18n.json" />
